import React, { Component } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Layers, Target, FileText, TrendingUp, ArrowRight } from 'lucide-react';
interface ContentFrameworkPageProps {
  onNavigate?: (path: string) => void;
}
export function ContentFrameworkPage({
  onNavigate
}: ContentFrameworkPageProps) {
  return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933]" data-id="element-781">
      <Navbar onNavigate={onNavigate} data-id="element-782" />

      <section className="py-24" data-id="element-783">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-784">
          <div className="text-center mb-16" data-id="element-785">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-786">
              Content Framework
            </h1>
            <p className="text-lg font-mono text-[#9ca3af] max-w-2xl mx-auto" data-id="element-787">
              Your custom blueprint for consistent, strategic content creation
            </p>
          </div>

          <div className="bg-white border-2 border-dashed border-salon-red p-12 mb-16" data-id="element-788">
            <h2 className="text-2xl font-bold mb-6 text-center" data-id="element-789">
              What is a Content Framework?
            </h2>
            <p className="text-lg text-[#1f2933]/80 max-w-3xl mx-auto text-center mb-8" data-id="element-790">
              A content framework is your personalized system for generating,
              organizing, and publishing content that aligns with your
              expertise, audience needs, and business goals. It's the difference
              between random posting and strategic authority building.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16" data-id="element-791">
            <div className="bg-white border-2 border-dashed border-[#9ca3af] p-8" data-id="element-792">
              <Layers className="w-12 h-12 text-salon-teal mb-4" data-id="element-793" />
              <h3 className="text-xl font-bold mb-4" data-id="element-794">Framework Components</h3>
              <ul className="space-y-3" data-id="element-795">
                <li className="flex items-start gap-3" data-id="element-796">
                  <span className="text-salon-teal font-bold" data-id="element-797">01</span>
                  <div data-id="element-798">
                    <h4 className="font-bold" data-id="element-799">Content Pillars</h4>
                    <p className="text-sm text-[#9ca3af]" data-id="element-800">
                      3-5 core themes that define your expertise
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-801">
                  <span className="text-salon-teal font-bold" data-id="element-802">02</span>
                  <div data-id="element-803">
                    <h4 className="font-bold" data-id="element-804">Content Types</h4>
                    <p className="text-sm text-[#9ca3af]" data-id="element-805">
                      Formats that work for your audience and style
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-806">
                  <span className="text-salon-teal font-bold" data-id="element-807">03</span>
                  <div data-id="element-808">
                    <h4 className="font-bold" data-id="element-809">Publishing Cadence</h4>
                    <p className="text-sm text-[#9ca3af]" data-id="element-810">
                      Sustainable frequency across platforms
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-811">
                  <span className="text-salon-teal font-bold" data-id="element-812">04</span>
                  <div data-id="element-813">
                    <h4 className="font-bold" data-id="element-814">Voice & Tone</h4>
                    <p className="text-sm text-[#9ca3af]" data-id="element-815">
                      Your unique communication style codified
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="space-y-8" data-id="element-816">
              <div className="bg-salon-yellow/10 border-2 border-salon-yellow p-6" data-id="element-817">
                <Target className="w-8 h-8 text-salon-yellow mb-3" data-id="element-818" />
                <h4 className="font-bold mb-2" data-id="element-819">Strategic Alignment</h4>
                <p className="text-sm text-[#1f2933]/80" data-id="element-820">
                  Every piece of content maps to your business goals, audience
                  needs, and authority positioning
                </p>
              </div>
              <div className="bg-salon-red/10 border-2 border-salon-red p-6" data-id="element-821">
                <FileText className="w-8 h-8 text-salon-red mb-3" data-id="element-822" />
                <h4 className="font-bold mb-2" data-id="element-823">Repeatable Process</h4>
                <p className="text-sm text-[#1f2933]/80" data-id="element-824">
                  Turn content creation from creative chaos into a reliable,
                  scalable system
                </p>
              </div>
              <div className="bg-salon-teal/10 border-2 border-salon-teal p-6" data-id="element-825">
                <TrendingUp className="w-8 h-8 text-salon-teal mb-3" data-id="element-826" />
                <h4 className="font-bold mb-2" data-id="element-827">Compound Growth</h4>
                <p className="text-sm text-[#1f2933]/80" data-id="element-828">
                  Build on previous content to create a body of work that
                  establishes authority
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#1f2933] text-white p-12" data-id="element-829">
            <h2 className="text-2xl font-bold mb-6" data-id="element-830">
              How We Build Your Framework
            </h2>
            <div className="grid md:grid-cols-3 gap-8 mb-8" data-id="element-831">
              <div data-id="element-832">
                <h3 className="font-bold mb-3 text-salon-red" data-id="element-833">Week 1-2</h3>
                <p className="text-sm text-[#f5f5f7]/80" data-id="element-834">
                  Discovery: Audit your expertise, audience, and goals. Define
                  your authority positioning.
                </p>
              </div>
              <div data-id="element-835">
                <h3 className="font-bold mb-3 text-salon-teal" data-id="element-836">Week 3-4</h3>
                <p className="text-sm text-[#f5f5f7]/80" data-id="element-837">
                  Design: Map content pillars, types, and cadence. Create your
                  editorial calendar structure.
                </p>
              </div>
              <div data-id="element-838">
                <h3 className="font-bold mb-3 text-salon-yellow" data-id="element-839">Week 5-6</h3>
                <p className="text-sm text-[#f5f5f7]/80" data-id="element-840">
                  Deploy: Integrate AI tools, build your content machine, and
                  start producing.
                </p>
              </div>
            </div>
            <div className="text-center" data-id="element-841">
              <Button size="large" onClick={() => onNavigate && onNavigate('/initiate-access')} className="bg-salon-red hover:bg-salon-red/90 border-salon-red" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-843" />} data-id="element-842">
                BUILD_YOUR_FRAMEWORK
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer onNavigate={onNavigate} data-id="element-844" />
    </div>;
}